/**
 * Human-readable rendering of `executions/{id}.artifactSkips` — the map of
 * `{ artifactKind: reasonCode }` the edge reports (via its capture-session skip
 * report) when a capture was NOT produced. Central-server persists it so the UI
 * can explain a missing artifact ("no signaling — still warming") instead of
 * showing a silently-empty panel. Shared by RunDetail and the Tests modal.
 */

const ARTIFACT_KIND_LABELS: Record<string, string> = {
  l3_trace: 'Signaling (SIP/SIB/LTE)',
  l1_trace: 'Raw DIAG trace (L1/L2)',
  pcap: 'Packet capture',
  voice_wav: 'Call recording',
};

export function artifactKindLabel(kind: string): string {
  return ARTIFACT_KIND_LABELS[kind] ?? kind;
}

export function artifactSkipReason(code: string): string {
  // `pi_load_<load>_over_<ceiling>` carries measured values → match by prefix.
  if (code.startsWith('pi_load_')) return 'the device was under high load';
  switch (code) {
    case 'diag_warming':
      return 'the signaling capture was still warming up (try re-running)';
    case 'at_only_modem':
      return 'this modem has no capturable data interface';
    case 'no_active_namespace':
      return 'no active data connection to capture from';
    case 'speedtest_leg':
      return 'speed-test legs run on the light capture so it does not slow the test';
    case 'deep_not_authorized':
      return 'deep capture is not authorized for this modem';
    case 'raw_capture_not_running':
      return 'the raw DIAG capture was not running';
    case 'no_raw_frames':
      return 'the modem sent no DIAG frames during the test';
    case 'upload_failed':
    case 'upload_error':
      return 'the trace was captured but its upload failed';
    case 'capture_ran_on_signaling':
      return 'the capture ran on the light mask';
    case 'superseded_by_new_allocation':
      return 'the capture session was replaced before it finished';
    default:
      return code.replace(/_/g, ' ');
  }
}

/**
 * Skips worth surfacing: only kinds that were skipped AND did not otherwise land
 * (a kind can be skipped on one leg and still be produced on another).
 */
export function visibleArtifactSkips(
  skips: Record<string, string> | undefined,
  presentTypes: Iterable<string>,
): Array<[string, string]> {
  if (!skips) return [];
  const present = new Set(presentTypes);
  return Object.entries(skips).filter(([kind]) => !present.has(kind));
}
